"use client";

import Image from "next/image";
import { CheckCircle } from "lucide-react";
import type { WaxUserAccount } from "@/lib/wax";
import type { WalletType, WalletOption } from "@/hooks/useWalletAuth";
import { WALLET_OPTIONS } from "@/hooks/useWalletAuth";

/**
 * Props for WalletSuccessDisplay component.
 */
export interface WalletSuccessDisplayProps {
  /** The authenticated account to display */
  account: WaxUserAccount;
  /** The wallet type used for authentication (optional) */
  walletType?: WalletType | null;
  /** Custom title (default: "Authentication Successful!") */
  title?: string;
  /** Custom description (default: based on wallet type) */
  description?: string;
  /** Visual variant */
  variant?: 'card' | 'compact' | 'inline';
  /** Show wallet icon */
  showWalletIcon?: boolean;
  /** Custom class name for container */
  className?: string;
}

/**
 * Get wallet info by type.
 */
function getWalletInfo(walletType: WalletType | string): WalletOption | undefined {
  return WALLET_OPTIONS.find(w => w.id === walletType || w.id === `wax-${walletType}`);
}

/**
 * Get display name for wallet type.
 */
function getWalletDisplayName(walletType: WalletType | string | null | undefined): string {
  if (!walletType) return 'WAX Account';
  const info = getWalletInfo(walletType);
  return info?.name || 'WAX Account';
}

/**
 * Get icon for wallet type.
 */
function getWalletIcon(walletType: WalletType | string | null | undefined): string {
  if (!walletType) return '💼';
  const info = getWalletInfo(walletType);
  return info?.icon || '💼';
}

/**
 * Shared success display component for wallet authentication.
 *
 * Consolidates the success UI from:
 * - WaxAuthenticator
 * - MultiWalletAuthenticator
 * - ExistingWalletLinker
 *
 * @example
 * ```tsx
 * <WalletSuccessDisplay
 *   account={authenticatedAccount}
 *   walletType="wax-cloud"
 *   variant="card"
 * />
 * ```
 */
export function WalletSuccessDisplay({
  account,
  walletType,
  title = "Authentication Successful!",
  description,
  variant = 'card',
  showWalletIcon = true,
  className = '',
}: WalletSuccessDisplayProps) {
  const avatarUrl = account.avatar ||
    `https://ui-avatars.com/api/?name=${account.accountName}&background=10b981&color=fff`;

  const walletName = getWalletDisplayName(walletType || account.walletType);
  const walletIcon = getWalletIcon(walletType || account.walletType);

  const finalDescription = description || `Successfully connected to ${walletName}`;

  if (variant === 'inline') {
    return (
      <div className={`flex items-center space-x-3 ${className}`}>
        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
        <Image
          src={avatarUrl}
          alt={`${account.accountName} avatar`}
          width={32}
          height={32}
          className="w-8 h-8 rounded-full"
          unoptimized
        />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-green-900 truncate">
            {account.accountName}
          </p>
          <p className="text-sm text-green-700 truncate">
            {walletName}
          </p>
        </div>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`bg-green-50 border border-green-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center space-x-3">
          {showWalletIcon && (
            <div className="text-2xl">{walletIcon}</div>
          )}
          <Image
            src={avatarUrl}
            alt={`${account.accountName} avatar`}
            width={40}
            height={40}
            className="w-10 h-10 rounded-full"
            unoptimized
          />
          <div>
            <p className="font-medium text-green-900">
              {account.accountName}
            </p>
            <p className="text-sm text-green-700">
              {walletName}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Default: card variant
  return (
    <div className={`bg-white rounded-lg shadow-lg border border-gray-200 p-6 ${className}`}>
      <div className="text-center mb-4">
        <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {title}
        </h3>
        <p className="text-gray-600 text-sm">
          {finalDescription}
        </p>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center justify-center space-x-3">
          {showWalletIcon && (
            <div className="text-2xl">{walletIcon}</div>
          )}
          <Image
            src={avatarUrl}
            alt={`${account.accountName} avatar`}
            width={40}
            height={40}
            className="w-10 h-10 rounded-full"
            unoptimized
          />
          <div className="text-left">
            <p className="font-medium text-green-900">
              {account.accountName}
            </p>
            <p className="text-sm text-green-700">
              {account.isTemp ? 'Temporary account' : 'Permanent account'}
              {account.walletType && ` • ${account.walletType}`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Error display component for wallet authentication failures.
 */
export interface WalletErrorDisplayProps {
  /** Error message to display */
  message: string;
  /** Retry callback */
  onRetry?: () => void;
  /** Custom title */
  title?: string;
  /** Visual variant */
  variant?: 'card' | 'compact' | 'inline';
  /** Custom class name */
  className?: string;
}

/**
 * Shared error display component for wallet authentication failures.
 */
export function WalletErrorDisplay({
  message,
  onRetry,
  title = "Authentication Failed",
  variant = 'card',
  className = '',
}: WalletErrorDisplayProps) {
  if (variant === 'inline') {
    return (
      <div className={`flex items-center space-x-2 text-red-700 ${className}`}>
        <svg className="h-5 w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
        <span className="text-sm">{message}</span>
        {onRetry && (
          <button
            onClick={onRetry}
            className="text-sm underline hover:no-underline"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`p-3 bg-red-50 border border-red-200 rounded-lg ${className}`}>
        <p className="text-sm text-red-700">{message}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="mt-2 text-sm text-red-600 underline hover:no-underline"
          >
            Try Again
          </button>
        )}
      </div>
    );
  }

  // Default: card variant
  return (
    <div className={`bg-white rounded-lg shadow-lg border border-gray-200 p-6 ${className}`}>
      <div className="text-center mb-4">
        <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {title}
        </h3>
        <p className="text-gray-600 text-sm">
          {message}
        </p>
      </div>

      {onRetry && (
        <button
          onClick={onRetry}
          className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
        >
          Try Again
        </button>
      )}
    </div>
  );
}

/**
 * Loading display component for wallet authentication.
 */
export interface WalletLoadingDisplayProps {
  /** Loading message */
  message?: string;
  /** Title */
  title?: string;
  /** Visual variant */
  variant?: 'card' | 'compact' | 'inline';
  /** Custom class name */
  className?: string;
}

/**
 * Shared loading display component for wallet authentication.
 */
export function WalletLoadingDisplay({
  message = "Please complete the authentication in your wallet...",
  title = "Authenticating...",
  variant = 'card',
  className = '',
}: WalletLoadingDisplayProps) {
  const spinner = (
    <svg className="w-6 h-6 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );

  if (variant === 'inline') {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        {spinner}
        <span className="text-sm text-gray-600">{message}</span>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`flex items-center space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg ${className}`}>
        {spinner}
        <div>
          <p className="font-medium text-blue-900">{title}</p>
          <p className="text-sm text-blue-700">{message}</p>
        </div>
      </div>
    );
  }

  // Default: card variant
  return (
    <div className={`bg-white rounded-lg shadow-lg border border-gray-200 p-6 ${className}`}>
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
          {spinner}
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {title}
        </h3>
        <p className="text-gray-600 text-sm">
          {message}
        </p>
      </div>
    </div>
  );
}

export default WalletSuccessDisplay;
